import { useEffect, useState } from "react";
import { Calendar, Flame, Snowflake, Target, Trophy } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@lyder-talk/ui";
import { useTranslation } from "react-i18next";

import { Mascot, useHomeMascotMood, MASCOT_ARIA_LABEL } from "@/components/mascot";
import type { UserGamification } from "@/hooks/use-gamification";
import { useStreakMilestones } from "@/hooks/use-gamification";
import type { DailyChallenge } from "@/hooks/use-daily-challenge";
import { cn } from "@/lib/utils";
import {
  hairlineClass,
  identityWashClass,
  segmentedItemActiveClass,
  segmentedItemClass,
  segmentedTrackClass,
  streakTileClass,
  streakTileIdleClass,
} from "@/lib/card-styles";
import { typeCaptionClass, typeNumericClass } from "@/lib/typography";

import { StreakCalendar } from "./StreakCalendar";
import { StreakChallenges } from "./StreakChallenges";
import { StreakMilestones } from "./StreakMilestones";
import { StreakFreezeStatus } from "./StreakFreezeStatus";

type StreakTab = "calendar" | "challenges" | "milestones" | "freezes";

interface StreakDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gamification: UserGamification | null | undefined;
  onClaimMilestone?: (
    milestoneId: string,
    badgeName: string,
    xpBonus: number,
    freezesAwarded: number,
  ) => void;
  onFreezeUsed?: (message: string) => void;
  challenges?: DailyChallenge[];
  challengesLoading?: boolean;
  claimingId?: string | null;
  onClaimReward?: (challengeId: string) => void;
  /** Tab to land on when the dialog opens — e.g. "challenges" from Home. */
  initialTab?: StreakTab;
}

const TABS: { id: StreakTab; labelKey: string; fallback: string; icon: typeof Calendar }[] = [
  { id: "calendar", labelKey: "pages.dialogs.calendar", fallback: "Calendar", icon: Calendar },
  {
    id: "challenges",
    labelKey: "pages.dialogs.challenges",
    fallback: "Challenges",
    icon: Target,
  },
  {
    id: "milestones",
    labelKey: "pages.dialogs.milestones",
    fallback: "Milestones",
    icon: Trophy,
  },
  { id: "freezes", labelKey: "pages.dialogs.freezes", fallback: "Freezes", icon: Snowflake },
];

/**
 * Streak detail — calendar, challenges, milestones, and freezes.
 *
 * ## The header had a stat wall
 *
 * It opened with a `bg-gradient-to-br from-orange-400 to-red-500` flame tile, a
 * `text-2xl font-bold` title, and a three-cell `bg-muted/50` grid for current /
 * longest / freezes. That grid is the stat wall the design system retired: three
 * equal-weight cells in a row, none of which is *the* number. The headline is
 * the streak, so the streak is the only figure that gets display size; longest
 * and freezes drop to `typeCaptionClass` beside it, separated by a middot. Same
 * information, a third of the height, and the eye lands on the one thing you
 * came to check.
 *
 * ## The flame tile is amber, and the header wears a wash
 *
 * `secondary` (amber) on an amber→orange tile, which is what the rest of the
 * product uses for streak and due state — an orange-to-red gradient square was
 * a gradient-for-decoration next to a mascot that is already animated. The
 * header takes `identityWashClass`, the stone→paper→amber background
 * `PageHero` uses under its serif title: a surface for the number to sit on,
 * not a button fill.
 *
 * ## The streak is the figure, so it is the only thing at display size
 *
 * The old header showed current / longest / freezes in three equal-weight
 * `bg-muted/50` cells — the stat wall the design system retired, where nothing
 * looked important because everything did. Longest and freezes drop to
 * `typeCaptionClass` beside the number. Same information, a third of the
 * height, and the eye lands on the one thing you came to check.
 *
 * ## Tabs are a segmented control, not a `bg-muted` grid
 *
 * The old strip was four buttons in a `bg-muted rounded-lg` box with its own
 * active-chip maths. `segmentedTrackClass` gives the same affordance from the
 * shared tokens, so the modal's switcher, the profile dialog's switcher, and
 * the theme switcher in the profile menu are visibly the same control.
 */
export const StreakDetailModal = ({
  open,
  onOpenChange,
  gamification,
  onClaimMilestone,
  onFreezeUsed,
  challenges = [],
  challengesLoading = false,
  claimingId = null,
  onClaimReward,
  initialTab,
}: StreakDetailModalProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<StreakTab>("calendar");

  // A caller can ask for a specific tab (Home's mobile "Daily challenges"
  // button lands on the challenges list). Applied on open only, so a tab the
  // learner then switches to is not yanked back underneath them.
  useEffect(() => {
    if (open && initialTab) setActiveTab(initialTab);
  }, [open, initialTab]);
  const { allMilestones } = useStreakMilestones({ enabled: open });
  const mascot = useHomeMascotMood();

  const streak = gamification?.current_streak ?? 0;
  const longestStreak = gamification?.longest_streak ?? 0;
  const freezesAvailable = gamification?.streak_freezes_available ?? 0;
  const freezesUsed = gamification?.streak_freezes_used ?? 0;
  const isActive = streak > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        variant="default"
        closeLabel={t("pages.dialogs.close", "Close")}
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
      >
        <DialogTitle className="sr-only">
          {t("gamification.streak.modalTitle", "Streak details")}
        </DialogTitle>

        {/* Header — the streak is the figure the dialog exists to show, so it
            is the only thing at display size. `identityWashClass` is the same
            stone→paper→amber background `PageHero` puts behind its title; it is
            a surface to sit text on, not a button fill, which is the one place
            the gradient ramp is allowed. Lydi is `sm` rather than `md` — at
            96px she out-shouted the number. */}
        <div
          className={cn(
            "relative flex shrink-0 items-center gap-4 overflow-hidden border-b px-5 py-5",
            hairlineClass,
            identityWashClass,
          )}
        >
          <span
            className={cn(
              "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl",
              isActive ? streakTileClass : streakTileIdleClass,
            )}
            aria-hidden="true"
          >
            <Flame className="h-7 w-7" />
          </span>

          <div className="min-w-0 flex-1">
            <h2 className="font-display text-2xl font-semibold leading-tight tracking-tight text-stone-900 dark:text-stone-50">
              {isActive ? (
                <>
                  <span className={cn("tabular-nums", typeNumericClass)}>{streak}</span>{" "}
                  {/* No `count` here on purpose: a count would make i18next
                      resolve the plural form and hand back the whole
                      "2 day streak" phrase, printing the number twice. This key
                      is the unit alone; the figure beside it is the count. */}
                  <span className="text-xl">
                    {t("gamification.streak.dayWord", "day streak")}
                  </span>
                </>
              ) : (
                t("gamification.streak.none", "No active streak")
              )}
            </h2>
            <p className={cn("mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5", typeCaptionClass)}>
              <span className={cn("tabular-nums font-semibold", typeNumericClass)}>
                {t("gamification.streak.longest", { defaultValue: "Longest {{count}}", count: longestStreak })}
              </span>
              <span className="text-stone-300 dark:text-stone-600" aria-hidden="true">
                ·
              </span>
              <span className={cn("tabular-nums font-semibold", typeNumericClass)}>
                {t("gamification.streak.freezes", {
                  defaultValue: "{{count}} freezes",
                  count: freezesAvailable,
                })}
              </span>
            </p>
          </div>

          <Mascot mood={mascot.mood} size="sm" ariaLabel={MASCOT_ARIA_LABEL[mascot.mood]} />
        </div>

        <div className="shrink-0 px-5 py-3">
          {/* Toggle group, not `role="tablist"` — the panel below it is a plain
              scroll area rather than a tabpanel, and this matches the theme
              switcher in the profile menu and the profile dialog's own switcher. */}
          <div
            role="group"
            aria-label={t("gamification.streak.sections", "Streak sections")}
            className={cn(segmentedTrackClass, "w-full")}
          >
            {TABS.map(({ id, labelKey, fallback, icon: Icon }) => (
              <button
                key={id}
                type="button"
                aria-pressed={activeTab === id}
                onClick={() => setActiveTab(id)}
                className={cn(segmentedItemClass, activeTab === id && segmentedItemActiveClass)}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {/* Icon-only below `sm`: four "icon + label" tabs need ~92px each,
                    a 390px dialog gives 87px, so "Freezes" ran off the screen.
                    The dialog is `w-full` up to `max-w-lg`, so its width tracks
                    the viewport and `sm:` is the right breakpoint here rather
                    than a container query.
                    The label uses `sr-only` (not `hidden`) so a screen reader on
                    a phone still announces the section — `display:none` drops the
                    text out of the a11y tree. `sm:not-sr-only` restores it on
                    desktop, where the label is drawn inline. */}
                <span className="truncate sr-only sm:not-sr-only">{t(labelKey, fallback)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
          {activeTab === "calendar" && (
            <StreakCalendar currentStreak={streak} allMilestones={allMilestones} />
          )}

          {activeTab === "challenges" && (
            <StreakChallenges
              challenges={challenges}
              isLoading={challengesLoading}
              claimingId={claimingId}
              onClaim={onClaimReward}
            />
          )}

          {activeTab === "milestones" && (
            <StreakMilestones
              currentStreak={streak}
              onClaimMilestone={onClaimMilestone}
            />
          )}

          {activeTab === "freezes" && (
            <StreakFreezeStatus
              freezesAvailable={freezesAvailable}
              freezesUsed={freezesUsed}
              currentStreak={streak}
              onFreezeUsed={onFreezeUsed}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StreakDetailModal;